import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { Link } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { getExpiringDocumentsForNotification, getEmployees } from "../../components/Api/GmsEmployee";
import { getEmployeesDueForLeaveCreditNotification } from "../../components/Api/GmsAnnualLeaveCreditHistory";
import { getBookingsWithEmployeesOnLeave } from "../../components/Api/GmsBooking";
import Breadcrumb from "../../components/Common/Breadcrumb";
import {
  hasPagePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";

const NotificationsPage = () => {
  const [activeTab, setActiveTab] = useState("1");
  const [expiringDocuments, setExpiringDocuments] = useState([]);
  const [leaveCreditNotifications, setLeaveCreditNotifications] = useState([]);
  const [bookingLeaveNotifications, setBookingLeaveNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const isAdmin = isUserAdmin();
  
  const hasExpiringDocsPermission = hasPagePermissionWithAdmin(permissions, '/GmsExpiringDocuments') || isAdmin;
  const hasLeaveCreditsPermission = hasPagePermissionWithAdmin(permissions, '/GmsAnnualLeaveCredit') || isAdmin;
  const hasBookingConflictsPermission = hasPagePermissionWithAdmin(permissions, '/GmsBooking') || isAdmin;

  useEffect(() => {
    // Set first permitted tab as active
    if (hasExpiringDocsPermission) {
      setActiveTab("1");
    } else if (hasLeaveCreditsPermission) {
      setActiveTab("2");
    } else if (hasBookingConflictsPermission) {
      setActiveTab("3");
    }
    
    loadAllNotifications();
  }, []);

  const loadAllNotifications = async () => {
    setLoading(true);
    try {
      // Load each notification type separately
      try {
        const documents = await getExpiringDocumentsForNotification();
        setExpiringDocuments(documents);
      } catch (error) {
        console.error("Error loading expiring documents:", error);
        setExpiringDocuments([]);
      }
      
      try {
        const leaveCredits = await getEmployeesDueForLeaveCreditNotification();
        setLeaveCreditNotifications(leaveCredits);
      } catch (error) {
        console.error("Error loading leave credits:", error);
        setLeaveCreditNotifications([]);
      }
      
      try {
        // Get user's companyId and branchId from localStorage
        const userCompanyId = localStorage.getItem("userCompanyId");
        const userBranchId = localStorage.getItem("userBranchId");
        
        // Build filter object - only include if values exist
        const filter = {};
        if (userCompanyId && userCompanyId !== 'null' && userCompanyId !== 'undefined') {
          filter.employee_Business_Id = parseInt(userCompanyId);
        }
        if (userBranchId && userBranchId !== 'null' && userBranchId !== 'undefined') {
          filter.employee_Branch_Id = parseInt(userBranchId);
        }
        
        // Get filtered employees to match against bookings
        const filteredEmployees = await getEmployees(filter);
        const filteredEmployeeIds = new Set(filteredEmployees.map(emp => emp.employee_Id));
        
        const bookingLeaves = await getBookingsWithEmployeesOnLeave();
        const bookings = bookingLeaves.data || [];
        
        // Filter bookings to only include those with employees matching company/branch filter
        // If no filter is set (companyId and branchId are null), show all bookings
        const filteredBookings = (userCompanyId || userBranchId) 
          ? bookings.filter(booking => filteredEmployeeIds.has(booking.employee_Id))
          : bookings;
        
        setBookingLeaveNotifications(filteredBookings);
      } catch (error) {
        console.error("Error loading booking leaves:", error);
        setBookingLeaveNotifications([]);
      }
      
    } catch (error) {
      console.error("Error loading notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getNotificationIcon = (documentType) => {
    switch (documentType) {
      case "Passport":
        return "bx bx-id-card";
      case "Visa":
        return "bx bx-plane";
      case "Nationality ID":
        return "bx bx-user-check";
      case "Labour Card":
        return "bx bx-briefcase";
      default:
        return "bx bx-file";
    }
  };

  const getNotificationColor = (isCritical) => {
    return isCritical ? "bg-danger" : "bg-warning";
  };

  const formatDaysLeft = (daysLeft) => {
    if (daysLeft < 0) {
      const daysAgo = Math.abs(daysLeft);
      return `Expired ${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago`;
    }
    return `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}`;
  };

  const totalNotifications = (hasExpiringDocsPermission ? expiringDocuments.length : 0) + 
                             (hasLeaveCreditsPermission ? leaveCreditNotifications.length : 0) + 
                             (hasBookingConflictsPermission ? bookingLeaveNotifications.length : 0);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="GMS" breadcrumbItem="All Notifications" />
        
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <div className="d-flex justify-content-between align-items-center">
                  <h4 className="card-title mb-0">
                    <i className="bx bx-bell me-2"></i>
                    All Notifications ({totalNotifications})
                  </h4>
                  <Button
                    color="primary"
                    onClick={loadAllNotifications}
                    disabled={loading}
                  >
                    {loading ? <ClipLoader size={16} color="#fff" /> : <i className="bx bx-refresh me-1"></i>}
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <Nav tabs className="nav-tabs-custom">
                  {hasExpiringDocsPermission && (
                    <NavItem>
                      <NavLink
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => setActiveTab("1")}
                        style={{ cursor: "pointer" }}
                      >
                        <i className="bx bx-file me-1"></i>
                        Expiring Documents ({expiringDocuments.length})
                      </NavLink>
                    </NavItem>
                  )}
                  {hasLeaveCreditsPermission && (
                    <NavItem>
                      <NavLink
                        className={activeTab === "2" ? "active" : ""}
                        onClick={() => setActiveTab("2")}
                        style={{ cursor: "pointer" }}
                      >
                        <i className="bx bx-calendar-check me-1"></i>
                        Leave Credits ({leaveCreditNotifications.length})
                      </NavLink>
                    </NavItem>
                  )}
                  {hasBookingConflictsPermission && (
                    <NavItem>
                      <NavLink
                        className={activeTab === "3" ? "active" : ""}
                        onClick={() => setActiveTab("3")}
                        style={{ cursor: "pointer" }}
                      >
                        <i className="bx bx-calendar-x me-1"></i>
                        Booking Conflicts ({bookingLeaveNotifications.length})
                      </NavLink>
                    </NavItem>
                  )}
                </Nav>

                <TabContent activeTab={activeTab}>
                  <TabPane tabId="1">
                    <div className="table-responsive">
                      <table className="table table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Employee</th>
                            <th>Document Type</th>
                            <th>Document Number</th>
                            <th>Expiry Date</th>
                            <th>Days Left</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expiringDocuments.map((doc, index) => (
                            <tr key={`doc-${index}`}>
                              <td>{doc.employeeName}</td>
                              <td>
                                <i className={`${getNotificationIcon(doc.documentType)} me-1`}></i>
                                {doc.documentType}
                              </td>
                              <td>{doc.documentNumber}</td>
                              <td>{formatDate(doc.expiryDate)}</td>
                              <td>
                                <span className={`badge ${doc.daysLeft < 0 ? 'bg-danger' : getNotificationColor(doc.isCritical)}`}>
                                  <span className={doc.daysLeft < 0 ? 'text-white fw-bold' : ''}>
                                    {formatDaysLeft(doc.daysLeft)}
                                  </span>
                                </span>
                              </td>
                            </tr>
                          ))}
                          {expiringDocuments.length === 0 && (
                            <tr>
                              <td colSpan="5" className="text-center py-4">
                                <i className="bx bx-check-circle text-success" style={{ fontSize: "2rem" }}></i>
                                <p className="mt-2 mb-0">No expiring documents</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </TabPane>

                  <TabPane tabId="2">
                    <div className="table-responsive">
                      <table className="table table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Employee</th>
                            <th>Employee Code</th>
                            <th>Last Credit Date</th>
                            <th>Days Overdue</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {leaveCreditNotifications.map((emp, index) => (
                            <tr key={`credit-${index}`}>
                              <td>{emp.employeeName}</td>
                              <td>{emp.employeeCode}</td>
                              <td>{formatDate(emp.lastCreditDate)}</td>
                              <td>
                                <span className={`badge ${getNotificationColor(emp.isCritical)}`}>
                                  {emp.daysOverdue} days
                                </span>
                              </td>
                              <td>
                                {emp.isCritical ? (
                                  <Badge color="danger">Critical</Badge>
                                ) : (
                                  <Badge color="warning">Warning</Badge>
                                )}
                              </td>
                              <td>
                                <Link 
                                  to="/GmsAnnualLeaveCredit" 
                                  className="btn btn-sm btn-primary"
                                >
                                  Process Credit
                                </Link>
                              </td>
                            </tr>
                          ))}
                          {leaveCreditNotifications.length === 0 && (
                            <tr>
                              <td colSpan="6" className="text-center py-4">
                                <i className="bx bx-check-circle text-success" style={{ fontSize: "2rem" }}></i>
                                <p className="mt-2 mb-0">No leave credit notifications</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </TabPane>

                  <TabPane tabId="3">
                    <div className="table-responsive">
                      <table className="table table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Employee</th>
                            <th>Customer</th>
                            <th>Booking Date</th>
                            <th>Leave Period</th>
                            <th>Leave Type</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bookingLeaveNotifications.map((booking, index) => (
                            <tr key={`booking-${index}`}>
                              <td>{booking.employeeName}</td>
                              <td>{booking.customerName}</td>
                              <td>{formatDate(booking.booking_Date)}</td>
                              <td>
                                {formatDate(booking.leave_FromDate)} to {formatDate(booking.leave_ToDate)}
                              </td>
                              <td>
                                <span className="badge bg-light text-dark border">{booking.leaveTypeName}</span>
                              </td>
                              <td>
                                <Link 
                                  to="/GmsBooking" 
                                  state={{ bookingId: booking.booking_Id, editMode: true }}
                                  className="btn btn-sm btn-warning"
                                >
                                  Edit Booking
                                </Link>
                              </td>
                            </tr>
                          ))}
                          {bookingLeaveNotifications.length === 0 && (
                            <tr>
                              <td colSpan="6" className="text-center py-4">
                                <i className="bx bx-check-circle text-success" style={{ fontSize: "2rem" }}></i>
                                <p className="mt-2 mb-0">No booking conflicts</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default NotificationsPage;
